/* Silent phase previews retain native product links on artwork and titles. */
(() => {
  if (window.momoOfferPreviewsLoaded) return;
  window.momoOfferPreviewsLoaded = true;

  const instances = new Map();
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let active = null;
  const inView = element => {
    const box = element.getBoundingClientRect();
    return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth;
  };
  const allowHover = event => finePointer.matches && !reducedMotion.matches && !navigator.connection?.saveData && event.pointerType !== 'touch';

  class Preview {
    constructor(root, button, label) {
      this.root = root;
      this.button = button;
      this.label = label;
      this.status = root.querySelector('[data-preview-status]');
      this.idleLabel = button.dataset.previewIdleLabel || root.dataset.previewIdleLabel || label.textContent.trim();
      this.idleAriaLabel = button.getAttribute('aria-label');
      this.isLink = button.matches('a[href]');
      this.version = 0;
      this.wanted = false;
      this.destroyed = false;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      button.addEventListener('click', event => {
        if (this.isLink) return;
        if (this.wanted) this.stop();
        else this.start(event.detail === 0 ? 'keyboard' : 'explicit');
      }, options);
      root.addEventListener('pointerenter', event => {
        if (allowHover(event)) this.start('hover');
      }, options);
      root.addEventListener('pointerleave', event => {
        if (this.intent === 'auto') return; // a silent loop does not stop when the mouse leaves
        // A finger leaving the screen must not undo its click.
        if (finePointer.matches && event.pointerType !== 'touch') this.stop();
      }, options);
      root.addEventListener('focusout', event => {
        if (this.intent === 'keyboard' && !root.contains(event.relatedTarget)) this.stop();
      }, options);
      root.setAttribute('data-preview-ready', 'true');
    }

    begin(intent) {
      if (this.destroyed || document.hidden || !inView(this.root)) return false;
      if (intent === 'hover' && (!finePointer.matches || reducedMotion.matches || navigator.connection?.saveData)) return false;
      if (intent === 'auto' && (reducedMotion.matches || navigator.connection?.saveData)) return false;
      if (this.wanted) return false;
      if (intent !== 'auto') {
        if (active && active !== this) active.stop();
        active = this;
      }
      this.intent = intent;
      this.wanted = true;
      this.loading = true;
      this.version++;
      this.setStatus('');
      this.render();
      return true;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    setLabel(text) {
      if (this.isLink) return;
      this.label.textContent = text;
      // Existing author-provided labels must follow the visible toggle state too.
      if (this.idleAriaLabel !== null) this.button.setAttribute('aria-label', this.wanted ? text : this.idleAriaLabel);
    }

    stop() {
      this.version++;
      this.wanted = false;
      this.loading = false;
      if (active === this) active = null;
      this.render();
    }

    fail(message) {
      this.stop();
      this.setStatus(message);
    }

    destroy() {
      this.stop();
      this.destroyed = true;
      this.events.abort();
      this.root.removeAttribute('data-preview-ready');
      observer?.unobserve(this.root);
      instances.delete(this.root);
    }
  }

  class VideoPreview extends Preview {
    constructor(root, video, button, label) {
      super(root, button, label);
      this.video = video;
      this.icon = button.querySelector('[data-preview-icon]');
      this.idleIcon = this.icon?.innerHTML;
      const options = { signal: this.events.signal };
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.addEventListener('play', () => {
        if (!this.wanted || (this.intent !== 'auto' && active !== this) || document.hidden || !inView(root)) {
          video.pause();
          return;
        }
        this.started = true;
      }, options);
      video.addEventListener('playing', () => {
        if (!this.wanted || (this.intent !== 'auto' && active !== this) || document.hidden || !inView(root)) {
          this.stop();
          return;
        }
        this.playing = true;
        this.loading = false;
        this.render();
      }, options);
      video.addEventListener('waiting', () => {
        if (this.wanted) {
          this.playing = false;
          this.loading = true;
          this.render();
        }
      }, options);
      video.addEventListener('pause', () => {
        // A different site player can pause this media without using our button.
        if (this.wanted && this.started && video.paused) this.stop();
      }, options);
      video.addEventListener('ended', () => this.stop(), options);
      video.addEventListener('error', () => {
        if (this.wanted) this.fail('preview couldn’t load. tap to try again.');
      }, options);
      this.render();
    }

    render() {
      const playing = Boolean(this.wanted && this.playing);
      this.root.classList.toggle('is-playing', playing);
      this.root.classList.toggle('is-loading', Boolean(this.wanted && this.loading));
      if (!this.isLink) this.button.setAttribute('aria-pressed', String(Boolean(this.wanted)));
      this.button.setAttribute('aria-busy', String(Boolean(this.wanted && this.loading)));
      this.setLabel(this.wanted ? 'pause preview' : this.idleLabel);
      if (this.icon) {
        if (this.wanted) this.icon.textContent = 'Ⅱ';
        else if (this.icon.innerHTML !== this.idleIcon) this.icon.innerHTML = this.idleIcon;
      }
    }

    async start(intent) {
      // Hover never interrupts a coaching lesson, testimonial, or another film.
      if (intent === 'hover' && [...document.querySelectorAll('video')].some(video => video !== this.video && !video.paused && !video.ended)) return;
      if (!this.begin(intent)) return;
      if (intent !== 'hover' && intent !== 'auto') document.querySelectorAll('video').forEach(video => { if (video !== this.video) video.pause(); }); // the silent loops run side by side; a deliberate play still stops everything else
      const attempt = this.version;
      this.started = false;
      this.playing = false;
      const source = this.video.dataset.src;
      if (!source) {
        this.fail('preview unavailable. open the program for details.');
        return;
      }
      try {
        if (this.video.getAttribute('src') !== source || this.video.error) {
          this.video.setAttribute('src', source);
          this.video.load();
        }
        await this.video.play();
        if (attempt !== this.version) {
          // An older promise must never pause a newer successful attempt.
          if (!this.wanted || (this.intent !== 'auto' && active !== this) || this.destroyed) this.video.pause();
          return;
        }
        if (document.hidden || !inView(this.root)) this.stop();
      } catch (error) {
        if (attempt !== this.version || this.destroyed) return;
        if (this.intent === 'auto') { this.stop(); return; } // a blocked silent loop keeps the cover, says nothing
        this.fail(error.name === 'NotAllowedError' ? 'tap preview to play.' : 'preview couldn’t load. tap to try again.');
      }
    }

    stop() {
      this.playing = false;
      this.started = false;
      super.stop();
      if (!this.video) return;
      this.video.pause();
      try { this.video.currentTime = 0; } catch (_) { /* No seekable media yet. Cover is already restored. */ }
    }
  }

  class ImagePreview extends Preview {
    constructor(root, layer, button, label) {
      super(root, button, label);
      this.layer = layer;
      this.image = layer.matches('img') ? layer : layer.querySelector('img');
      this.render();
    }

    render() {
      const showing = Boolean(this.wanted && this.showing);
      this.root.classList.toggle('is-previewing', showing);
      this.root.classList.toggle('is-loading', Boolean(this.wanted && this.loading));
      this.button.setAttribute('aria-expanded', String(showing));
      this.button.setAttribute('aria-busy', String(Boolean(this.wanted && this.loading)));
      this.layer?.setAttribute('aria-hidden', String(!showing));
      this.setLabel(this.wanted ? 'show cover' : this.idleLabel);
    }

    async start(intent) {
      if (!this.begin(intent)) return;
      const attempt = this.version;
      try {
        if (!this.image) throw new Error('Missing preview image');
        if (!this.image.complete) {
          if (typeof this.image.decode === 'function') await this.image.decode();
          else await new Promise((resolve, reject) => {
            this.image.addEventListener('load', resolve, { once: true, signal: this.events.signal });
            this.image.addEventListener('error', reject, { once: true, signal: this.events.signal });
          });
        }
        if (attempt !== this.version || !this.wanted || this.destroyed) return;
        if (!this.image.naturalWidth) throw new Error('Preview image failed');
        if (document.hidden || !inView(this.root)) { this.stop(); return; }
        this.showing = true;
        this.loading = false;
        this.render();
      } catch (_) {
        if (attempt === this.version && !this.destroyed) this.fail('preview couldn’t load. open the program for details.');
      }
    }

    stop() {
      this.showing = false;
      super.stop();
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const preview = instances.get(entry.target);
      if (!preview) return;
      if (!entry.isIntersecting) { preview.stop(); return; }
      // 16 Oct · quiet covers: a phase preview loops silently once a third of it is on screen [Inner Unit's tiles]
      if (entry.intersectionRatio >= 0.34 && entry.target.hasAttribute('data-offer-autoplay') && !preview.wanted) preview.start('auto');
    });
  }, { threshold: [0, 0.34] }) : null;

  function initialize(scope = document) {
    const selector = '[data-offer-preview], [data-offer-image-preview]';
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      let preview;
      if (root.matches('[data-offer-preview]')) {
        const video = root.querySelector('video');
        const button = root.querySelector('[data-offer-preview-toggle]');
        const label = button?.querySelector('[data-preview-label]');
        if (video && button && label) preview = new VideoPreview(root, video, button, label);
      } else {
        const layer = root.querySelector('.mo-preview-layer');
        const button = root.querySelector('[data-offer-image-toggle]');
        const label = button?.querySelector('[data-image-preview-label]');
        if (layer && button && label) preview = new ImagePreview(root, layer, button, label);
      }
      if (preview) { instances.set(root, preview); observer?.observe(root); }
    });
  }

  const stopDisallowedHover = () => {
    if (active?.intent === 'hover' && (!finePointer.matches || reducedMotion.matches || navigator.connection?.saveData)) active.stop();
    if (reducedMotion.matches || navigator.connection?.saveData) instances.forEach(preview => { if (preview.intent === 'auto') preview.stop(); });
  };
  reducedMotion.addEventListener?.('change', stopDisallowedHover);
  finePointer.addEventListener?.('change', stopDisallowedHover);
  navigator.connection?.addEventListener?.('change', stopDisallowedHover);
  if (!observer) {
    const stopOffscreen = () => { if (active && !inView(active.root)) active.stop(); };
    window.addEventListener('scroll', stopOffscreen, { passive: true });
    window.addEventListener('resize', stopOffscreen, { passive: true });
  }
  document.addEventListener('visibilitychange', () => { if (document.hidden) instances.forEach(preview => preview.stop()); });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') active?.stop(); });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach(preview => { if (event.target === preview.root || event.target.contains(preview.root)) preview.destroy(); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
